import { Box, Image, Loader, Search } from "@wix/design-system";
import classNames from "classnames";
import lodash from "lodash";
import React, { useContext, useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroller";
import { PluginsEventsContext, RicosContext, useDevGiphyRequestService } from "./../../ricos-context";
import { GIFData_GIFType } from "./../../ricos-schema/next-schema";
import { useBiNext } from "./../../_shared/bi-context-provider/bi-context-provider";
import { uuid } from "./../../wix-rich-content-common/index";
import {
  imagePlaceholder,
  PAGE_SIZE,
  RICOS_DEFAULTS,
  WAIT_INTERVAL
} from "./../constants";
import {
  INSERT_GIF_MODAL_GIF_ITEM,
  INSERT_GIF_MODAL_GIF_SEARCH_INPUT,
  INSERT_GIF_MODAL_GIF_SELECTOR,
  INSERT_GIF_MODAL_STICKER_ITEM,
  INSERT_GIF_MODAL_STICKER_SEARCH_INPUT
} from "../data-hooks";
import { PoweredByGiphy } from "../icons";
import { GIPHY_TYPE } from "../types";
import { getGiphyProxyUrl } from "./get-giphy-proxy-url";
import { GiphyEmptyState } from "./giphy-empty-state";
import { GiphyErrorState } from "./giphy-error-state";
import styles from "./giphy-selector-scss";
const convertGiphyToPmNode = (giphy) => {
  return {
    containerData: { ...RICOS_DEFAULTS.containerData },
    original: {
      gif: giphy.images.original.url,
      mp4: giphy.images.original.mp4,
      still: giphy.images.original_still.url
    },
    downsized: {
      gif: giphy.images.downsized?.url,
      mp4: giphy.images.downsized_small?.mp4,
      still: giphy.images.downsized_still.url
    },
    height: Number(giphy.images.original.height),
    width: Number(giphy.images.original.width),
    gifType: giphy.is_sticker ? GIFData_GIFType.STICKER : GIFData_GIFType.NORMAL
  };
};
const GiphySelector = ({
  onGifAdd,
  onCloseRequested,
  getGiphyUrl,
  category,
  modalRef,
  modalId
}) => {
  const [modalWidth, setModalWidth] = useState(0);
  const { t, isMobile } = useContext(RicosContext);
  const devGiphyRequestService = useDevGiphyRequestService();
  const [state, setState] = useState({
    type: "LOADING",
    searchTag: "",
    requestId: uuid()
  });
  const logBi = useBiNext({ modalId, pluginId: "giphy" });
  const pluginsEvents = useContext(PluginsEventsContext);
  const triggerBi = lodash.debounce(() => {
    pluginsEvents.publishPluginPopoverSearch({ pluginId: GIPHY_TYPE, searchTerm: state.searchTag });
  });
  useEffect(() => {
    if (modalRef) {
      const { width } = modalRef.getBoundingClientRect();
      setModalWidth(width);
    }
  }, [modalRef]);
  useEffect(() => {
    searchGifs("");
  }, []);
  const searchGifs = (searchTag) => {
    triggerBi();
    const requestId = uuid();
    setState({
      type: "LOADING",
      searchTag,
      requestId
    });
    getGifs(searchTag, 0, requestId);
  };
  const fetchTrendingGifs = async (page) => {
    if (devGiphyRequestService) {
      const { results } = await devGiphyRequestService.getTrendingGifs({
        path: "trending",
        category,
        limit: 100,
        offset: page * 100
      });
      return results;
    }
    const url = getGiphyProxyUrl(getGiphyUrl, {
      path: "trending",
      category,
      limit: 100,
      offset: page * 100
    });
    if (!url) {
      return setState({
        type: "ERROR",
        searchTag: ""
      });
    }
    const result = await fetch(url).then(
      async (r) => r.status === 200 ? { type: "OK", data: await r.json() } : Promise.reject()
    ).catch(() => ({ type: "ERROR" }));
    if (result.type === "ERROR") {
      return setState({
        type: "ERROR",
        searchTag: ""
      });
    }
    return result.data;
  };
  const fetchSearchGifs = async (searchTag, page) => {
    if (devGiphyRequestService) {
      const { results } = await devGiphyRequestService.getSearchGifs({
        path: "search",
        category,
        limit: PAGE_SIZE + 1,
        offset: page * PAGE_SIZE,
        searchTag
      });
      return results;
    }
    const url = getGiphyProxyUrl(getGiphyUrl, {
      path: "search",
      category,
      limit: PAGE_SIZE + 1,
      offset: page * PAGE_SIZE,
      searchTag
    });
    if (!url) {
      return setState({
        type: "ERROR",
        searchTag: ""
      });
    }
    const result = await fetch(url).then(
      async (r) => r.status === 200 ? { type: "OK", data: await r.json() } : Promise.reject()
    ).catch(() => ({ type: "ERROR" }));
    if (result.type === "ERROR") {
      return setState({
        type: "ERROR",
        searchTag: ""
      });
    }
    return result.data;
  };
  const processNewGifs = (gifsInfo, page, requestId) => {
    const newGifs = gifsInfo.data;
    const currentTabOrder = state.type === "READY" && page !== 0 ? state.totalLength : 0;
    const updatedColumns = newGifs.reduce(
      (columns, gif, index) => {
        const { height } = gif.images.fixed_width_downsampled;
        const columnIndex = columns[0].totalHeight <= columns[1].totalHeight ? 0 : 1;
        columns[columnIndex].gifs.push({
          ...gif,
          tabOrder: currentTabOrder + index
        });
        columns[columnIndex].totalHeight += Number(height);
        return columns;
      },
      page === 0 ? [
        { gifs: [], totalHeight: 0 },
        { gifs: [], totalHeight: 0 }
      ] : [...state.columns]
    );
    setState((prev) => {
      if (prev.type === "LOADING" && prev.requestId !== requestId) {
        return prev;
      }
      return {
        type: "READY",
        columns: updatedColumns,
        hasMoreItems: newGifs.length < gifsInfo.pagination.total_count,
        page: page + 1,
        totalLength: updatedColumns[0].gifs.length + updatedColumns[1].gifs.length,
        searchTag: prev.searchTag
      };
    });
  };
  const getGifs = async (searchTag, page, requestId) => {
    const data = searchTag ? await fetchSearchGifs(searchTag, page) : await fetchTrendingGifs(page);
    if (data) {
      processNewGifs(data, page, requestId);
    }
  };
  const selectGif = (gif) => {
    logBi({
      dataHook: category === "gifs" ? INSERT_GIF_MODAL_GIF_ITEM : INSERT_GIF_MODAL_STICKER_ITEM
    });
    onGifAdd(gif);
    onCloseRequested();
  };
  const onClick = (giphy) => {
    const componentData = convertGiphyToPmNode(giphy);
    selectGif(componentData);
  };
  const handleNavigation = (tabOrder, offset) => {
    if (!modalRef?.parentElement) {
      return;
    }
    const gifs = state.type === "READY" ? state.columns.flatMap((col) => col.gifs) : [];
    const targetGif = gifs.find((gif) => gif.tabOrder === tabOrder + offset);
    if (targetGif) {
      const gifElement = modalRef.parentElement.querySelector(
        `[data-id="${targetGif.id}${targetGif.tabOrder}"]`
      );
      if (gifElement) {
        gifElement.focus();
      }
    }
  };
  const onKeyDown = (e, giphy) => {
    if (e.key === "Tab" && !e.shiftKey || e.key === "ArrowRight") {
      e.preventDefault();
      handleNavigation(giphy.tabOrder || 0, 1);
    }
    if (e.shiftKey && e.key === "Tab" || e.key === "ArrowLeft") {
      if (giphy.tabOrder === 0) {
        return;
      }
      e.preventDefault();
      handleNavigation(giphy.tabOrder || 0, -1);
    }
    if (e.key === "Escape") {
      onCloseRequested?.();
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const componentData = convertGiphyToPmNode(giphy);
      selectGif(componentData);
    }
  };
  return /* @__PURE__ */ React.createElement(Box, { direction: "vertical", padding: "SP3", height: "430px" }, /* @__PURE__ */ React.createElement(
    Search,
    {
      placeholder: category === "gifs" ? t("GiphyUploadModal_Search_Placeholder") : t("GiphyUploadModal_Search_Placeholder_Stickers"),
      onChange: (e) => {
        logBi({
          dataHook: category === "gifs" ? INSERT_GIF_MODAL_GIF_SEARCH_INPUT : INSERT_GIF_MODAL_STICKER_SEARCH_INPUT,
          value: e.target.value
        });
        searchGifs(e.target.value);
      },
      debounceMs: WAIT_INTERVAL,
      onClear: () => searchGifs(""),
      value: state.searchTag,
      dataHook: category === "gifs" ? INSERT_GIF_MODAL_GIF_SEARCH_INPUT : INSERT_GIF_MODAL_STICKER_SEARCH_INPUT,
      className: styles.searchInput
    }
  ), state.type === "ERROR" ? /* @__PURE__ */ React.createElement("div", { "data-hook": INSERT_GIF_MODAL_GIF_SELECTOR }, /* @__PURE__ */ React.createElement(GiphyErrorState, null)) : /* @__PURE__ */ React.createElement("div", { "data-hook": INSERT_GIF_MODAL_GIF_SELECTOR }, /* @__PURE__ */ React.createElement(
    "div",
    {
      className: classNames(styles.giphy_selector_infinite_scroll_container, {
        [styles.mobile]: isMobile
      })
    },
    state.type === "READY" && !state.columns[0].gifs.length && state.searchTag ? /* @__PURE__ */ React.createElement(GiphyEmptyState, { category }) : state.type === "LOADING" ? /* @__PURE__ */ React.createElement(Box, { width: "100%", height: "100%", align: "center", verticalAlign: "middle" }, /* @__PURE__ */ React.createElement(Loader, { size: "small" })) : state.type === "READY" ? /* @__PURE__ */ React.createElement(
      InfiniteScroll,
      {
        pageStart: 0,
        loadMore: async () => getGifs(state.searchTag, state.page, uuid()),
        hasMore: state.hasMoreItems,
        loader: /* @__PURE__ */ React.createElement("div", { className: styles.giphy_selector_spinner_load_more }, /* @__PURE__ */ React.createElement(Loader, { size: "small" })),
        useWindow: false,
        className: state.columns[0].gifs.length > 0 ? styles.giphy_selector_infinite_scroll : styles.giphy_selector_spinner_empty_modal
      },
      /* @__PURE__ */ React.createElement(Box, { gap: "18px", display: "flex", verticalAlign: "space-between" }, state.columns.map((column, columnIndex) => /* @__PURE__ */ React.createElement(Box, { key: columnIndex, display: "flex", direction: "vertical", gap: "18px" }, column.gifs.map((giphy) => /* @__PURE__ */ React.createElement(
        Image,
        {
          key: giphy.id.toString(),
          "data-id": `${giphy.id.toString()}${giphy.tabOrder}`,
          tabIndex: 0,
          transparent: true,
          onKeyDown: (e) => onKeyDown(e, giphy),
          onClick: () => onClick(giphy),
          dataHook: category === "gifs" ? INSERT_GIF_MODAL_GIF_ITEM : INSERT_GIF_MODAL_STICKER_ITEM,
          id: giphy.id.toString(),
          role: "img",
          "aria-label": category,
          width: giphy.images.fixed_width_downsampled.width,
          height: calculateHeight(giphy, modalWidth),
          fit: "fill",
          src: giphy.images.fixed_width_downsampled.webp || giphy.images.fixed_width_downsampled.url || imagePlaceholder,
          className: styles.giphy_selector_gif_img_container,
          showBorder: true,
          borderRadius: "8px",
          alt: giphy.alt_text || category
        }
      )))))
    ) : null
  ), /* @__PURE__ */ React.createElement(Box, { display: "block", width: "100%", paddingTop: "8px" }, /* @__PURE__ */ React.createElement(PoweredByGiphy, { className: styles.giphy_selector_powerdByGiphy }))));
};
const calculateHeight = (giphy, modalWidth) => {
  const originalHeight = Number(giphy.images.fixed_width_downsampled.height);
  const originalWidth = Number(giphy.images.fixed_width_downsampled.width);
  const paddings = 18 * 3;
  const newWidth = (modalWidth - paddings) / 2;
  return originalHeight / originalWidth * newWidth;
};
export {
  GiphySelector
};
//# sourceMappingURL=giphy-selector.js.map
